import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, MouseEvent } from 'react'

import SmoothDropdown from '../../Animated/SmoothDropdownBehaivor/SmoothDropdown'
import usePopup from '../../hooks/usePopup'

import Checkbox from '../Checkbox'
import Tag from 'shared/Tag'

import { ReactComponent as ArrowDown } from 'assets/icons/triangleDown.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/IconClose.svg'

import cn from '../../../utils/cn'
import css from './dropdown.module.css'
import InfinityLoader, { InfinityLoaderProps } from 'shared/Loaders/InfinityLoader'

/* -------------------------------------------------------------------------- */
/*                                   types                                    */
/* -------------------------------------------------------------------------- */

type ISelectedOption<T, Multiple extends boolean> = Multiple extends true ? T[] : T | null

export interface DropdownProps<T, Multiple extends boolean = false> {
  options: T[]
  selectedOption: ISelectedOption<T, Multiple>
  title: string | ReactNode
  name?: string
  menuStyle?: CSSProperties
  loading?: boolean
  allowEmpty?: boolean
  disabled?: boolean
  virtual?: boolean
  multiple?: Multiple
  onSelect: (value: ISelectedOption<T, Multiple>, name?: string) => void
  /** Функция получения лейбла из опции */
  getOptionLabel?: (option: T) => string
  /**
   * Функция получения ключа из опции. Если не передана,
   * используется `getOptionLabel`, иначе `String(option)`.
   */
  getOptionKey?: (option: T) => string | number
  infinityLoadProps?: InfinityLoaderProps
  limitTags?: number
}

/* -------------------------------------------------------------------------- */
/*                                компонент                                   */
/* -------------------------------------------------------------------------- */

function Dropdown<T, Multiple extends boolean = false>(props: DropdownProps<T, Multiple>) {
  /* --------------------------- refs / popup state -------------------------- */
  const rootRef = useRef<HTMLDivElement | null>(null)
  const { open, setOpen } = usePopup(rootRef)
  const selectedRef = useRef<ISelectedOption<T, Multiple>>(props.selectedOption)

  /* ------------------------- вспомогательные функции ----------------------- */

  /** Берём label / key согласно приоритетам */
  const getLabel = useCallback(
    (o: T) => (props.getOptionLabel ? props.getOptionLabel(o) : String(o)),
    [props.getOptionLabel]
  )

  const getKey = useCallback(
    (o: T) => {
      return props.getOptionKey ? props.getOptionKey(o) : getLabel(o)
    },
    [props.getOptionKey, getLabel]
  )

  const getBase = useCallback((o: T) => ({ key: getKey(o), label: getLabel(o) }), [getKey, getLabel])

  /* ------------------------- утилиты выбранности -------------------------- */

  const isSelected = useCallback(
    (key: string | number) => {
      if (props.multiple) {
        const selected = props.selectedOption as ISelectedOption<T, true>
        return selected.some((o) => getBase(o).key === key)
      }
      const selected = props.selectedOption as ISelectedOption<T, false>
      return selected ? getBase(selected).key === key : false
    },
    [props.multiple, props.selectedOption, getBase]
  )

  const hasSelected = useMemo(() => {
    if (props.multiple) {
      return (props.selectedOption as ISelectedOption<T, true>).length > 0
    }
    return Boolean(props.selectedOption)
  }, [props.selectedOption, props.multiple])

  /* ------------------------------- handlers -------------------------------- */

  const toggleOption =
    (option: T | null) =>
    (e?: MouseEvent): void => {
      e?.stopPropagation()
      if (props.disabled) return

      if (props.multiple) {
        if (option === null) return
        const { key } = getBase(option)
        const selected = props.selectedOption as ISelectedOption<T, true>
        const exists = selected.some((s) => getBase(s).key === key)

        const result = exists
          ? (selected.filter((s) => getBase(s).key !== key) as ISelectedOption<T, Multiple>)
          : ([...selected, option] as ISelectedOption<T, Multiple>)

        props.onSelect(result, props.name)
        return
      }

      props.onSelect(option as ISelectedOption<T, Multiple>, props.name)
      setOpen(false)
    }

  // типы у checkbox не подходят
  const handleCheckOption = (o: T) => () => toggleOption(o)()

  const clearAll = (e: MouseEvent) => {
    e.stopPropagation()
    if (!props.multiple) return
    props.onSelect([] as ISelectedOption<T, Multiple>, props.name)
  }

  /* ----------------------- клавиатурное управление ------------------------ */

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  /* --------------------------- options rendering --------------------------- */

  const optionNodes = useMemo(
    () => (
      <div>
        {!props.multiple &&
          props.allowEmpty !== false &&
          hasSelected && ( // «Не выбрано» показываем только если allowEmpty и что-то выбрано
            <div
              className={cn('text--body-s', css.option)}
              role='option'
              aria-selected={false}
              onClick={toggleOption(null)}
            >
              Не выбрано
            </div>
          )}
        {props.options.map((o) => {
          const { key, label } = getBase(o)
          const active = isSelected(key)

          return (
            <div
              key={key}
              className={cn('inline-flex', css.option)}
              data-active={props.multiple ? false : active}
              onClick={toggleOption(o)}
            >
              {props.multiple && <Checkbox checked={active} onCheck={handleCheckOption(o)} />}
              <span className='text--body-s'>{label}</span>
            </div>
          )
        })}
      </div>
    ),
    [(props.options, props.multiple, getBase, isSelected, toggleOption)]
  )

  /* --------------------------- selected rendering -------------------------- */

  const renderSingle = (label: string | ReactNode, placeholder?: boolean) => (
    <span
      className={cn('text--body-s line_clamp', placeholder && 'text--placeholder')}
      title={typeof label === 'string' ? label : undefined}
    >
      {label}
    </span>
  )

  const selectedContent = useMemo(() => {
    if (props.multiple) {
      const selected = props.selectedOption as ISelectedOption<T, true>
      const sliceParams = (props.limitTags && !open) ? [0, props.limitTags] : []
      return selected.length
        ? selected.slice(...sliceParams).map((o) => {
            const { key, label } = getBase(o)
            return <Tag key={key} label={label} onDelete={toggleOption(o)} />
          })
        : renderSingle(props.title, true)
    }

    const single = props.selectedOption as ISelectedOption<T, false>
    if (!single) return renderSingle(props.title, true)
    return renderSingle(getBase(single).label)
  }, [props.selectedOption, props.multiple, getBase, props.title, toggleOption])

  /* ----------------------------- misc effects ----------------------------- */

  useEffect(() => {
    selectedRef.current = props.selectedOption
  }, [props.selectedOption])

  /* ------------------------------ component -------------------------------- */

  const showClearBtn = props.multiple && open && hasSelected

  return (
    <div
      ref={rootRef}
      className={css.wrapper}
      data-open={open}
      data-loading={props.loading}
      onClick={() => !props.loading && setOpen((p) => !p)}
    >
      <div className={css.selectedWrapper}>{selectedContent}</div>

      {showClearBtn && (
        <div onClick={clearAll}>
          <CloseIcon style={{ width: '12px', height: '12px' }} />
        </div>
      )}

      {!props.disabled && !showClearBtn && (
        <div className={css.icon}>
          <ArrowDown data-open={open} data-selected={hasSelected} className={css.arrow} />
        </div>
      )}

      <SmoothDropdown open={open} className={css.options} maxHeight={300} style={props.menuStyle} overflow='auto'>
        {optionNodes}
        {props.infinityLoadProps && (
          <InfinityLoader {...props.infinityLoadProps}>
            <div />
          </InfinityLoader>
        )}
      </SmoothDropdown>
    </div>
  )
}

/* -------------------------------- export ---------------------------------- */

export default Dropdown
